"use client";

import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Home, ArrowLeft, Search, FileText, Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion"; // Added for smooth animations

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname,
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-white to-muted/20 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary/10 rounded-full blur-3xl animate-pulse animation-delay-2000"></div>
      </div>

      <div className="container max-w-4xl mx-auto px-4 py-12 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left side: Error message and buttons */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center md:text-left"
          >
            <motion.div 
              initial={{ scale: 0.8, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, type: "spring" }}
              className="flex justify-center md:justify-start mb-6"
            >
              <div className="p-6 bg-gradient-to-r from-destructive/10 to-destructive/20 rounded-2xl shadow-lg">
                <AlertCircle className="h-20 w-20 text-destructive drop-shadow-lg" />
              </div>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-6xl md:text-8xl font-bold mb-4 text-foreground bg-gradient-to-r from-foreground to-muted-foreground/50 bg-clip-text text-transparent"
            >
              404
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-xl text-muted-foreground mb-6 max-w-md mx-auto md:mx-0"
            >
              Oops! The page you're looking for doesn't exist.
            </motion.p>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg text-muted-foreground mb-8 max-w-md mx-auto md:mx-0"
            >
              It might have been moved, renamed, or perhaps it never existed in the first place. Let's get you back on track.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
            >
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <Link to="/" className="flex items-center space-x-2">
                  <Home className="h-5 w-5" />
                  Go to Dashboard
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <Link to="/" className="flex items-center space-x-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Home
                </Link>
              </Button>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-8 p-6 bg-gradient-to-r from-muted/50 to-accent/20 rounded-2xl border border-border/50"
            >
              <p className="text-sm text-muted-foreground mb-2">
                Need help? <Link to="/contact" className="text-primary hover:underline font-medium">Contact our support team</Link> or check our <Link to="/docs" className="text-primary hover:underline font-medium">documentation</Link>.
              </p>
            </motion.div>
          </motion.div>

          {/* Right side: Animated tips section */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="hidden md:block"
          >
            <div className="bg-gradient-to-r from-destructive/5 to-destructive/10 rounded-2xl p-8 shadow-xl border border-destructive/20">
              <motion.h3 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.8 }}
                className="text-lg font-semibold mb-6 text-center text-foreground"
              >
                Helpful Tips
              </motion.h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.9 }}
                  className="flex flex-col items-center space-y-3 p-4 bg-white/50 rounded-lg"
                >
                  <div className="p-3 bg-primary/10 rounded-full">
                    <Search className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground">Double-check the URL</p>
                    <p className="text-xs text-muted-foreground">Typos happen to the best of us.</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 1.0 }}
                  className="flex flex-col items-center space-y-3 p-4 bg-white/50 rounded-lg"
                >
                  <div className="p-3 bg-secondary/10 rounded-full">
                    <FileText className="h-5 w-5 text-secondary" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground">Try searching</p>
                    <p className="text-xs text-muted-foreground">Use the search bar in your dashboard.</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 1.1 }}
                  className="flex flex-col items-center space-y-3 p-4 bg-white/50 rounded-lg"
                >
                  <div className="p-3 bg-accent/10 rounded-full">
                    <Clock className="h-5 w-5 text-accent" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground">Recent pages</p>
                    <p className="text-xs text-muted-foreground">Check your browser history.</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 1.2 }}
                  className="flex flex-col items-center space-y-3 p-4 bg-white/50 rounded-lg"
                >
                  <div className="p-3 bg-muted/10 rounded-full">
                    <AlertTriangle className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground">Clear cache</p>
                    <p className="text-xs text-muted-foreground">Sometimes a fresh start helps.</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;